import { useEffect, useMemo, useState } from 'react'
import BaseModal from '../../../components/BaseModal'
import AppCombobox from '../../../components/AppCombobox'
import { appConfirm } from '../../../components/AppConfirmModal'
import {
  listPlatformStaff,
  listTenantUsers,
  grantPlatformRole,
  revokePlatformRole,
} from '../api/supportTicketApi'

const ROLE_OPTIONS = [
  {
    value: 'SUPPORT_AGENT',
    label: 'Support Agent',
    description: 'Can view customer tickets, update status, and respond to conversations.',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  {
    value: 'DEVELOPER',
    label: 'Platform Developer',
    description: 'Full agent capabilities with developer visibility for technical investigations.',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  {
    value: 'SUPER_ADMIN',
    label: 'Super Admin',
    description: 'Unrestricted control: assign/reassign tickets and manage platform staff.',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
  },
]

export default function PlatformStaffManagementModal({
  isOpen,
  onClose,
  tenants = [],
  onStaffUpdated,
}) {
  const [activeTab, setActiveTab] = useState('list') // 'list' | 'add'
  const [staffList, setStaffList] = useState([])
  const [isLoadingStaff, setIsLoadingStaff] = useState(false)
  const [actionError, setActionError] = useState('')
  const [actionSuccess, setActionSuccess] = useState('')

  // Add/Convert Form State
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [tenantUsers, setTenantUsers] = useState([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedRole, setSelectedRole] = useState('SUPPORT_AGENT')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load staff on modal open
  const fetchStaff = async () => {
    setIsLoadingStaff(true)
    setActionError('')
    try {
      const data = await listPlatformStaff()
      setStaffList(Array.isArray(data) ? data : [])
    } catch (err) {
      setActionError(err?.message || 'Failed to load platform staff roster.')
    } finally {
      setIsLoadingStaff(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchStaff()
      setActiveTab('list')
      setSelectedTenantId('')
      setTenantUsers([])
      setSelectedUserId('')
      setSelectedRole('SUPPORT_AGENT')
      setActionError('')
      setActionSuccess('')
    }
  }, [isOpen])

  // Fetch users whenever selected tenant changes
  useEffect(() => {
    if (!selectedTenantId) {
      setTenantUsers([])
      setSelectedUserId('')
      return
    }
    let active = true
    setIsLoadingUsers(true)
    setSelectedUserId('')
    listTenantUsers(selectedTenantId)
      .then((users) => {
        if (active) {
          setTenantUsers(Array.isArray(users) ? users : [])
        }
      })
      .catch((err) => {
        if (active) {
          setActionError(err?.message || 'Failed to load users for this tenant.')
          setTenantUsers([])
        }
      })
      .finally(() => {
        if (active) setIsLoadingUsers(false)
      })
    return () => {
      active = false
    }
  }, [selectedTenantId])

  const tenantOptions = useMemo(() => {
    return (tenants || []).map((t) => ({
      value: String(t.id),
      label: t.name || t.code || `Tenant #${t.id}`,
    }))
  }, [tenants])

  const userOptions = useMemo(() => {
    return (tenantUsers || []).map((u) => {
      const desig = u.designation ? ` (${u.designation})` : ''
      return {
        value: String(u.id),
        label: `${u.name}${desig} — ${u.email}`,
        email: u.email,
      }
    })
  }, [tenantUsers])

  const handleGrantRole = async (e) => {
    e?.preventDefault()
    if (!selectedTenantId) {
      setActionError('Please select a customer tenant.')
      return
    }
    if (!selectedUserId) {
      setActionError('Please select a user from the tenant.')
      return
    }
    const chosenUser = tenantUsers.find((u) => String(u.id) === String(selectedUserId))
    if (!chosenUser || !chosenUser.email) {
      setActionError('Selected user does not have a valid email address.')
      return
    }

    try {
      setIsSubmitting(true)
      setActionError('')
      setActionSuccess('')
      await grantPlatformRole({
        tenant_id: Number(selectedTenantId),
        user_id: Number(selectedUserId),
        email: chosenUser.email,
        role: selectedRole,
      })
      setActionSuccess(`Successfully assigned ${chosenUser.name} as ${selectedRole.replace('_', ' ')}!`)
      setSelectedUserId('')
      await fetchStaff()
      onStaffUpdated?.()
      setActiveTab('list')
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || 'Failed to grant platform role.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRevokeRole = async (staffMember) => {
    const confirmed = await appConfirm({
      title: 'Revoke Platform Access',
      message: `Are you sure you want to revoke platform access for ${staffMember.email}?`,
      confirmText: 'Revoke',
      variant: 'danger',
    })
    if (!confirmed) {
      return
    }
    try {
      setActionError('')
      setActionSuccess('')
      await revokePlatformRole(staffMember.id)
      setActionSuccess(`Revoked platform access for ${staffMember.email}.`)
      await fetchStaff()
      onStaffUpdated?.()
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || 'Failed to revoke platform staff.')
    }
  }

  const renderRoleBadge = (role) => {
    const opt = ROLE_OPTIONS.find((r) => r.value === role)
    const cls = opt ? opt.badgeClass : 'bg-surface-container text-on-surface-variant border-outline-variant/30'
    const label = opt ? opt.label : role
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${cls}`}>
        {label}
      </span>
    )
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Platform Staff & Agent Management"
      subtitle="Super Admin control panel to convert and assign users to platform roles across all customer tenants."
      widthClassName="max-w-3xl"
    >
      <div className="space-y-5">
        {/* Alerts */}
        {actionError && (
          <div className="rounded-xl border border-error/30 bg-error-container/20 p-3 text-xs font-semibold text-error flex items-center justify-between">
            <span>{actionError}</span>
            <button type="button" onClick={() => setActionError('')} className="text-error font-bold">×</button>
          </div>
        )}
        {actionSuccess && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-xs font-semibold text-emerald-800 flex items-center justify-between">
            <span>{actionSuccess}</span>
            <button type="button" onClick={() => setActionSuccess('')} className="text-emerald-800 font-bold">×</button>
          </div>
        )}

        {/* Modal Tabs */}
        <div className="flex items-center gap-2 border-b border-outline-variant/20 pb-3">
          <button
            type="button"
            onClick={() => { setActiveTab('list'); setActionError(''); setActionSuccess('') }}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
              activeTab === 'list'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            <span className="material-symbols-outlined text-base">groups</span>
            Active Staff ({staffList.filter((s) => s.isActive).length})
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('add'); setActionError(''); setActionSuccess('') }}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
              activeTab === 'add'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            <span className="material-symbols-outlined text-base">person_add</span>
            Convert User to Agent
          </button>
        </div>

        {/* Tab 1: Staff Roster */}
        {activeTab === 'list' && (
          <div className="space-y-3">
            {isLoadingStaff ? (
              <div className="py-12 text-center text-sm text-on-surface-variant">
                Loading platform staff roster...
              </div>
            ) : staffList.length === 0 ? (
              <div className="py-12 text-center text-sm text-on-surface-variant">
                No platform staff found. Use &quot;Convert User to Agent&quot; to assign staff members.
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-surface-container text-on-surface-variant font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">User & Email</th>
                        <th className="px-4 py-3">Tenant / Company</th>
                        <th className="px-4 py-3">Platform Role</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {staffList.map((member) => (
                        <tr key={member.id} className="hover:bg-surface-container-low/50 transition-colors">
                          <td className="px-4 py-3 font-medium text-on-surface">
                            <div className="font-bold">{member.email}</div>
                            <div className="text-[11px] text-on-surface-variant">User ID: #{member.userId}</div>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">
                            {member.tenantName || `Tenant #${member.tenantId}`}
                          </td>
                          <td className="px-4 py-3">{renderRoleBadge(member.role)}</td>
                          <td className="px-4 py-3">
                            {member.isActive ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                              </span>
                            ) : (
                              <span className="text-on-surface-variant/60 italic">Revoked</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {member.isActive && (
                              <button
                                type="button"
                                onClick={() => handleRevokeRole(member)}
                                className="inline-flex items-center gap-1 rounded-md border border-error/30 bg-error/5 px-2 py-1 text-[11px] font-bold text-error hover:bg-error/10 transition-colors cursor-pointer"
                              >
                                <span className="material-symbols-outlined text-xs">person_remove</span>
                                Revoke
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Convert User to Agent Form */}
        {activeTab === 'add' && (
          <form onSubmit={handleGrantRole} className="space-y-4 rounded-xl border border-outline-variant/20 bg-surface-container-low p-4">
            <h4 className="text-sm font-bold text-on-surface">Convert Existing User to Platform Agent</h4>
            <p className="text-xs text-on-surface-variant">
              Select the customer tenant, choose an active user from that tenant database, and assign their platform privileges.
            </p>

            {/* 1. Tenant Selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
                Step 1: Select Customer Tenant <span className="text-error">*</span>
              </label>
              <AppCombobox
                options={tenantOptions}
                value={selectedTenantId}
                onChange={(val) => {
                  setSelectedTenantId(val ? String(val) : '')
                  setActionError('')
                }}
                placeholder="Choose tenant company..."
                searchPlaceholder="Search tenant by name..."
                allowClear
              />
            </div>

            {/* 2. User Selector */}
            {selectedTenantId && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
                  Step 2: Select User from Tenant Database <span className="text-error">*</span>
                </label>
                {isLoadingUsers ? (
                  <div className="py-2 text-xs text-on-surface-variant italic">Loading users for this tenant...</div>
                ) : userOptions.length === 0 ? (
                  <div className="py-2 text-xs text-error font-medium">No active users found in this tenant.</div>
                ) : (
                  <AppCombobox
                    options={userOptions}
                    value={selectedUserId}
                    onChange={(val) => {
                      setSelectedUserId(val ? String(val) : '')
                      setActionError('')
                    }}
                    placeholder="Search and choose user..."
                    searchPlaceholder="Search by name, designation, or email..."
                    allowClear
                  />
                )}
              </div>
            )}

            {/* 3. Role Selector */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
                Step 3: Assign Platform Role <span className="text-error">*</span>
              </label>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {ROLE_OPTIONS.map((opt) => {
                  const isChecked = selectedRole === opt.value
                  return (
                    <label
                      key={opt.value}
                      className={`relative flex flex-col p-3 rounded-xl border cursor-pointer transition-all ${
                        isChecked
                          ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-xs'
                          : 'border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-high'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-on-surface">{opt.label}</span>
                        <input
                          type="radio"
                          name="platform_role_option"
                          value={opt.value}
                          checked={isChecked}
                          onChange={() => setSelectedRole(opt.value)}
                          className="h-3.5 w-3.5 text-primary focus:ring-primary"
                        />
                      </div>
                      <p className="text-[11px] text-on-surface-variant/80 leading-relaxed">{opt.description}</p>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-outline-variant/20">
              <button
                type="button"
                onClick={() => setActiveTab('list')}
                className="rounded-xl border border-outline-variant/30 px-4 py-2 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
              >
                Back to Staff List
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !selectedTenantId || !selectedUserId}
                className="rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow hover:bg-primary/90 transition-colors disabled:opacity-40 cursor-pointer flex items-center gap-2"
              >
                {isSubmitting && (
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                )}
                Grant Platform Role
              </button>
            </div>
          </form>
        )}
      </div>
    </BaseModal>
  )
}
